import { ScreenBase } from "@frui.ts/screens";
import { interfaces } from "inversify";
import { computed, observable } from "mobx";
import LocalizationService from "services/localizationService";
import { getValidationMessage } from "utils/validationPatch";
import ChargeOrderChargeItem from "models/chargeOrderChargeItem";
import ChargeTypeResponse from "entities/chargeTypeResponse";
import ChargeOrderService from "services/chargeOrderService";
import ChargeTypesService from "services/chargeTypesService";
import FuelTypeResponse from "entities/fuelTypeResponse";
import EnumService from "services/enumService";
import FuelType from "entities/fuelType";

export default class ChargeTypeViewModel extends ScreenBase {
  @observable batchIndex: number | undefined;
  @observable chargeType = new ChargeTypeResponse();
  @observable chargeOrderChargeItemId: string | undefined;

  @computed
  get isCSLocale() {
    return this.localizationService.locale == "cs";
  }

  constructor(
    chargeType: ChargeTypeResponse,
    public localizationService: LocalizationService,
    public chargeOrderChargeItem: ChargeOrderChargeItem,
    private chargeOrderService: ChargeOrderService,
    private chargeTypesService: ChargeTypesService,
    private enumService: EnumService,
    chargeOrderChargeItemId: string | undefined
  ) {
    super();
    this.chargeType = chargeType;
    this.chargeOrderChargeItemId = chargeOrderChargeItemId;
  }

  @computed
  get chargeTypeCode() {
    return `${this.chargeType.id}`;
  }

  @computed
  get error() {
    return getValidationMessage(this.chargeOrderChargeItem, "chargeTypeId");
  }

  @computed
  get description() {
    return this.localizationService.translateAttribute("chargeType", this.chargeType.validFor);
  }

  @computed
  get fuelTypes() {
    return this.enumService.values("fuel_types") as FuelTypeResponse[];
  }

  @computed
  get chargeTypes() {
    return this.enumService.values("charge_types") as ChargeTypeResponse[];
  }

  @computed
  get price() {
    const chargeOrderItem = this.chargeOrderService.chargeOrder.charges.length
      ? this.chargeOrderService.chargeOrderItems.find(storageItem => storageItem.id === this.chargeOrderChargeItemId)
      : this.chargeOrderService.chargeOrderItem;
    if (chargeOrderItem && chargeOrderItem?._priceListItems?.length && chargeOrderItem._fuelTypeId) {
      this.chargeType._standardPrice = chargeOrderItem?._priceListItems.find(
        item => item.chargeTypeId === this.chargeType.id && item.fuelTypeId === chargeOrderItem._fuelTypeId
      )?.price;
    } else {
      const validFuelType = this.fuelTypes.find(
        fuelType =>
          this.chargeTypesService.chargePriceList?.includedFuelTypeIds.includes(fuelType.id.toString()) &&
          !fuelType.isDeleted &&
          fuelType.fuelType === FuelType.Other
      );
      const validChargeType = this.chargeTypes.find(chargeType => chargeType.validFor === this.chargeType.validFor);
      const price = this.chargeTypesService.chargePriceList.items.find(
        item => item.chargeTypeId === validChargeType?.id && item.fuelTypeId === validFuelType?.id
      );
      this.chargeType._standardPrice = price?.price;
    }

    return this.localizationService.formatPrice(this.chargeType._standardPrice ?? 0);
  }

  static Factory({ container }: interfaces.Context) {
    return (chargeType: ChargeTypeResponse, chargeOrderChargeItem: ChargeOrderChargeItem, chargeOrderChargeItemId?: string) => {
      return new ChargeTypeViewModel(
        chargeType,
        container.get(LocalizationService),
        chargeOrderChargeItem,
        container.get(ChargeOrderService),
        container.get(ChargeTypesService),
        container.get(EnumService),
        chargeOrderChargeItemId
      );
    };
  }
}
